import React from 'react';
import styled from 'styled-components';
import { FiPhone, FiMail, FiMapPin, FiFacebook, FiInstagram, FiChevronRight } from 'react-icons/fi';
import { LuMountain } from 'react-icons/lu';
import colors from '../../styles/colors';

const FooterSection = styled.footer`
  background: ${colors.background.main};
  padding: 80px 40px 30px;

  @media (max-width: 768px) {
    padding: 60px 20px 30px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr 1fr;
  gap: 50px;
  margin-bottom: 60px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 500px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div``;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
`;

const LogoIcon = styled.div`
  width: 45px;
  height: 45px;
  background: ${colors.gradient.gold};
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  color: ${colors.primary.dark};
`;

const LogoText = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.1;
`;

const LogoMain = styled.span`
  font-size: 1.2rem;
  font-weight: 800;
  color: ${colors.text.white};
`;

const LogoSub = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${colors.accent.gold};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Description = styled.p`
  font-size: 0.95rem;
  color: ${colors.text.light};
  line-height: 1.8;
  margin-bottom: 25px;
  opacity: 0.8;
`;

const Socials = styled.div`
  display: flex;
  gap: 12px;
`;

const SocialLink = styled.a`
  width: 45px;
  height: 45px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.text.light};
  font-size: 1.2rem;
  transition: all 0.3s ease;

  &:hover {
    background: ${colors.accent.gold};
    color: ${colors.primary.dark};
  }
`;

const ColumnTitle = styled.h4`
  font-size: 1.1rem;
  color: ${colors.text.white};
  margin-bottom: 25px;
  font-weight: 700;
  position: relative;
  padding-bottom: 12px;

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 35px;
    height: 3px;
    background: ${colors.accent.gold};
    border-radius: 2px;
  }
`;

const LinksList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LinkItem = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${colors.text.light};
  font-size: 0.95rem;
  opacity: 0.8;
  cursor: pointer;
  transition: all 0.3s ease;

  svg {
    font-size: 0.8rem;
    color: ${colors.accent.gold};
    opacity: 0.6;
  }

  &:hover {
    opacity: 1;
    color: ${colors.accent.gold};
    transform: translateX(5px);

    svg {
      opacity: 1;
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 18px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactIcon = styled.div`
  width: 42px;
  height: 42px;
  background: rgba(201, 162, 39, 0.15);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.accent.gold};
  font-size: 1rem;
  flex-shrink: 0;
`;

const ContactText = styled.div`
  p {
    font-family: 'Quicksand', sans-serif;
    font-size: 0.9rem;
    color: ${colors.text.light};
    line-height: 1.5;
    opacity: 0.8;
  }

  a {
    font-family: 'Quicksand', sans-serif;
    color: ${colors.text.light};
    text-decoration: none !important;
    opacity: 0.8;

    &:hover {
      color: ${colors.accent.gold};
      opacity: 1;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(255, 255, 255, 0.1);
  margin-bottom: 30px;
`;

const Bottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;

  @media (max-width: 600px) {
    justify-content: center;
    text-align: center;
  }
`;

const Copyright = styled.p`
  color: ${colors.text.light};
  font-size: 0.85rem;
  opacity: 0.6;
`;

const BottomLinks = styled.div`
  display: flex;
  gap: 25px;

  a {
    font-family: 'Quicksand', sans-serif;
    color: ${colors.text.light};
    font-size: 0.85rem;
    text-decoration: none !important;
    opacity: 0.6;

    &:hover {
      opacity: 1;
      color: ${colors.accent.gold};
    }
  }
`;

export default function Footer() {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <FooterSection>
      <Container>
        <Grid>
          <Column>
            <Logo>
              <LogoIcon><LuMountain /></LogoIcon>
              <LogoText>
                <LogoMain>Greenridge</LogoMain>
                <LogoSub>Landscaping Co.</LogoSub>
              </LogoText>
            </Logo>
            <Description>
              Serving the community with quality landscaping services since 2005.
              Your trusted partner for all your outdoor needs.
            </Description>
            <Socials>
              <SocialLink href="#" aria-label="Facebook">
                <FiFacebook />
              </SocialLink>
              <SocialLink href="#" aria-label="Instagram">
                <FiInstagram />
              </SocialLink>
            </Socials>
          </Column>

          <Column>
            <ColumnTitle>Quick Links</ColumnTitle>
            <LinksList>
              <LinkItem onClick={() => scrollTo('hero')}>
                <FiChevronRight /> Home
              </LinkItem>
              <LinkItem onClick={() => scrollTo('services')}>
                <FiChevronRight /> Services
              </LinkItem>
              <LinkItem onClick={() => scrollTo('about')}>
                <FiChevronRight /> About Us
              </LinkItem>
              <LinkItem onClick={() => scrollTo('work')}>
                <FiChevronRight /> Our Work
              </LinkItem>
              <LinkItem onClick={() => scrollTo('contact')}>
                <FiChevronRight /> Contact
              </LinkItem>
            </LinksList>
          </Column>

          <Column>
            <ColumnTitle>Services</ColumnTitle>
            <LinksList>
              <LinkItem onClick={() => scrollTo('services')}>
                <FiChevronRight /> Garden Center
              </LinkItem>
              <LinkItem onClick={() => scrollTo('services')}>
                <FiChevronRight /> Landscape Design
              </LinkItem>
              <LinkItem onClick={() => scrollTo('services')}>
                <FiChevronRight /> Lawn Maintenance
              </LinkItem>
              <LinkItem onClick={() => scrollTo('services')}>
                <FiChevronRight /> Snow Removal
              </LinkItem>
            </LinksList>
          </Column>

          <Column>
            <ColumnTitle>Contact</ColumnTitle>
            <ContactItem>
              <ContactIcon><FiMapPin /></ContactIcon>
              <ContactText>
                <p>742 Elm Street<br />Essex, VT 05452</p>
              </ContactText>
            </ContactItem>
            <ContactItem>
              <ContactIcon><FiPhone /></ContactIcon>
              <ContactText>
                <p><a href="tel:5559876543">(555) 987-6543</a></p>
              </ContactText>
            </ContactItem>
            <ContactItem>
              <ContactIcon><FiMail /></ContactIcon>
              <ContactText>
                <p><a href="mailto:hello@greenridgelandscaping.com">hello@greenridgelandscaping.com</a></p>
              </ContactText>
            </ContactItem>
          </Column>
        </Grid>

        <Divider />

        <Bottom>
          <Copyright>
            &copy; {new Date().getFullYear()} Greenridge Landscaping Co. All rights reserved.
          </Copyright>
          <BottomLinks>
            <a href="#privacy">Privacy Policy</a>
            <a href="#terms">Terms of Service</a>
          </BottomLinks>
        </Bottom>
      </Container>
    </FooterSection>
  );
}
